import {
  type ReactNode,
  type HTMLAttributes,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from 'react'

import { clsx } from 'clsx'

/**
 * Sort direction type
 */
export type SortDirection = 'asc' | 'desc' | null

/**
 * Sort state for table
 */
export interface SortState {
  column: string | null
  direction: SortDirection
}

/**
 * Table context for sharing state
 */
interface TableContextValue {
  selectable: boolean
  selectedRows: Set<string>
  onSelectRow: (id: string) => void
  onSelectAll: () => void
  allRowIds: string[]
  sortState: SortState
  onSort: (column: string) => void
}

const TableContext = createContext<TableContextValue | null>(null)

function useTableContext(): TableContextValue {
  const context = useContext(TableContext)
  /* v8 ignore next 3 */
  if (!context) {
    throw new Error('Table components must be used within a Table')
  }
  return context
}

// ============================================================================
// Pure handler helpers
// ============================================================================

function toggleRowSelection(id: string, current: Set<string>): Set<string> {
  const next = new Set(current)
  if (next.has(id)) { next.delete(id) } else { next.add(id) }
  return next
}

function toggleAllSelection(rowIds: string[], current: Set<string>): Set<string> {
  return rowIds.every((id) => current.has(id)) ? new Set<string>() : new Set(rowIds)
}

function nextSortState(current: SortState, column: string): SortState {
  let newDirection: SortDirection = 'asc'
  if (current.column === column) {
    if (current.direction === 'asc') { newDirection = 'desc' }
    else if (current.direction === 'desc') { newDirection = null }
  }
  return { column: newDirection ? column : null, direction: newDirection }
}

function resolveAriaSortValue(
  isSorted: boolean,
  direction: SortDirection | null | undefined
): 'ascending' | 'descending' | undefined {
  if (!isSorted) { return undefined }
  return direction === 'asc' ? 'ascending' : 'descending'
}

// ============================================================================
// Table state hook
// ============================================================================

interface UseTableStateResult {
  selectedRows: Set<string>
  sortState: SortState
  handleSelectRow: (id: string) => void
  handleSelectAll: () => void
  handleSort: (column: string) => void
}

interface UseTableStateArgs {
  selectable: boolean
  selectedRowsProp: string[]
  onSelectionChange?: (ids: string[]) => void
  rowIds: string[]
  sortStateProp?: SortState
  onSortChange?: (state: SortState) => void
}

function useTableState({
  selectedRowsProp,
  onSelectionChange,
  rowIds,
  sortStateProp,
  onSortChange,
}: UseTableStateArgs): UseTableStateResult {
  const [internalSelected, setInternalSelected] = useState<Set<string>>(new Set())
  const [internalSort, setInternalSort] = useState<SortState>({ column: null, direction: null })

  const selectedRows = useMemo(
    () => (onSelectionChange ? new Set(selectedRowsProp) : internalSelected),
    [onSelectionChange, selectedRowsProp, internalSelected]
  )
  /* v8 ignore next */
  const sortState = useMemo(
    () => (onSortChange ? (sortStateProp ?? { column: null, direction: null }) : internalSort),
    [onSortChange, sortStateProp, internalSort]
  )

  const handleSelectRow = useCallback(
    (id: string) => {
      const next = toggleRowSelection(id, selectedRows)
      if (onSelectionChange) { onSelectionChange(Array.from(next)) }
      else { setInternalSelected(next) }
    },
    [selectedRows, onSelectionChange]
  )

  const handleSelectAll = useCallback(() => {
    const next = toggleAllSelection(rowIds, selectedRows)
    if (onSelectionChange) { onSelectionChange(Array.from(next)) }
    /* v8 ignore next 3 */
    else { setInternalSelected(next) }
  }, [rowIds, selectedRows, onSelectionChange])

  const handleSort = useCallback(
    (column: string) => {
      const next = nextSortState(sortState, column)
      if (onSortChange) { onSortChange(next) }
      else { setInternalSort(next) }
    },
    [sortState, onSortChange]
  )

  return { selectedRows, sortState, handleSelectRow, handleSelectAll, handleSort }
}

// ============================================================================
// Table Root
// ============================================================================

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /** Enable row selection */
  selectable?: boolean
  /** Currently selected row IDs */
  selectedRows?: string[]
  /** Callback when selection changes */
  onSelectionChange?: (selectedIds: string[]) => void
  /** All row IDs (for select all functionality) */
  rowIds?: string[]
  /** Current sort state */
  sortState?: SortState
  /** Callback when sort changes */
  onSortChange?: (state: SortState) => void
  children: ReactNode
}

/**
 * Table component following elBooks UI/UX standards
 *
 * Features:
 * - Sortable columns with visual indicators
 * - Row selection with checkbox
 * - Right-aligned numbers per accounting standards
 * - Responsive design
 *
 * @example
 * // Basic table
 * <Table>
 *   <TableHeader>
 *     <TableRow>
 *       <TableHead>Entry #</TableHead>
 *       <TableHead>Date</TableHead>
 *       <TableHead align="right">Amount</TableHead>
 *     </TableRow>
 *   </TableHeader>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>JE-001</TableCell>
 *       <TableCell>2024-01-15</TableCell>
 *       <TableCell align="right">1,000.00</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 */
export function Table({
  selectable = false,
  selectedRows: selectedRowsProp = [],
  onSelectionChange,
  rowIds = [],
  sortState: sortStateProp,
  onSortChange,
  className,
  children,
  ...props
}: TableProps): JSX.Element {
  const { selectedRows, sortState, handleSelectRow, handleSelectAll, handleSort } = useTableState({
    selectable,
    selectedRowsProp,
    onSelectionChange,
    rowIds,
    sortStateProp,
    onSortChange,
  })

  return (
    <TableContext.Provider
      value={{
        selectable,
        selectedRows,
        onSelectRow: handleSelectRow,
        onSelectAll: handleSelectAll,
        allRowIds: rowIds,
        sortState,
        onSort: handleSort,
      }}
    >
      <div className="table-wrapper">
        <table
          className={clsx('w-full border-collapse', 'text-sm text-foreground', className)}
          {...props}
        >
          {children}
        </table>
      </div>
    </TableContext.Provider>
  )
}

// ============================================================================
// Table Header
// ============================================================================

export interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode
}

export function TableHeader({ className, children, ...props }: TableHeaderProps): JSX.Element {
  return (
    <thead className={clsx('bg-muted', 'border-b border-border', className)} {...props}>
      {children}
    </thead>
  )
}

// ============================================================================
// Table Body
// ============================================================================

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode
}

export function TableBody({ className, children, ...props }: TableBodyProps): JSX.Element {
  return (
    <tbody className={clsx('divide-y divide-border', className)} {...props}>
      {children}
    </tbody>
  )
}

// ============================================================================
// Table Row
// ============================================================================

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Row ID for selection */
  rowId?: string
  /** Whether this row is selected */
  selected?: boolean
  children: ReactNode
}

export function TableRow({ rowId, selected: selectedProp, className, children, ...props }: TableRowProps): JSX.Element {
  const context = useContext(TableContext)
  const isSelected = selectedProp ?? (rowId && context?.selectedRows.has(rowId))

  return (
    <tr
      className={clsx('hover:bg-muted', 'transition-colors duration-150', isSelected && 'bg-accent', className)}
      {...props}
    >
      {children}
    </tr>
  )
}

// ============================================================================
// Table Head (Header Cell)
// ============================================================================

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Column key for sorting */
  sortKey?: string
  /** Enable sorting for this column */
  sortable?: boolean
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  children?: ReactNode
}

const alignClassMap = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const

function SortIndicator({ direction }: { direction: SortDirection | null | undefined }): JSX.Element {
  return (
    <span aria-hidden="true" className="text-muted-foreground">
      {direction === 'asc' && '▲'}
      {direction === 'desc' && '▼'}
      {!direction && '⇅'}
    </span>
  )
}

export function TableHead({
  sortKey,
  sortable = false,
  align = 'left',
  className,
  children,
  ...props
}: TableHeadProps): JSX.Element {
  const context = useContext(TableContext)
  const isSorted = Boolean(sortKey && context?.sortState.column === sortKey)
  const sortDirection = isSorted ? context?.sortState.direction : null

  const handleClick = (): void => {
    if (sortable && sortKey && context) { context.onSort(sortKey) }
  }

  return (
    <th
      aria-sort={resolveAriaSortValue(isSorted, sortDirection)}
      className={clsx(
        'px-4 py-3 font-semibold text-muted-foreground',
        alignClassMap[align],
        sortable && 'cursor-pointer select-none hover:text-foreground',
        className
      )}
      onClick={sortable ? handleClick : undefined}
      {...props}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortable && <SortIndicator direction={sortDirection} />}
      </span>
    </th>
  )
}

// ============================================================================
// Table Cell
// ============================================================================

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Text alignment */
  align?: 'left' | 'center' | 'right'
  /** Use monospace font (for numbers) */
  mono?: boolean
  children?: ReactNode
}

export function TableCell({ align = 'left', mono = false, className, children, ...props }: TableCellProps): JSX.Element {
  return (
    <td
      className={clsx('px-4 py-3', alignClassMap[align], mono && 'font-mono tabular-nums', className)}
      {...props}
    >
      {children}
    </td>
  )
}

// ============================================================================
// Table Checkbox Cell
// ============================================================================

export interface TableCheckboxProps {
  /** Row ID for selection */
  rowId?: string
  /** Whether this is for selecting all rows (header) */
  isHeader?: boolean
}

export function TableCheckbox({ rowId, isHeader = false }: TableCheckboxProps): JSX.Element | null {
  const context = useTableContext()

  if (!context.selectable) { return null }

  const isSelected = rowId
    ? context.selectedRows.has(rowId)
    : context.allRowIds.every((id) => context.selectedRows.has(id))

  const isIndeterminate = isHeader &&
    context.selectedRows.size > 0 &&
    context.selectedRows.size < context.allRowIds.length

  const handleChange = (): void => {
    if (isHeader) { context.onSelectAll() }
    else if (rowId) { context.onSelectRow(rowId) }
  }

  const CellComponent = isHeader ? 'th' : 'td'

  return (
    <CellComponent className="py-3 w-12">
      <input
        ref={(el) => { if (el) { el.indeterminate = isIndeterminate } }}
        aria-label={isHeader ? 'Select all rows' : `Select row ${rowId}`}
        checked={isSelected}
        className={clsx('h-4 w-4 rounded', 'border-border', 'text-primary focus:ring-ring', 'cursor-pointer')}
        type="checkbox"
        onChange={handleChange}
      />
    </CellComponent>
  )
}

// ============================================================================
// Export all components
// ============================================================================

export default Table
